import React, { useState } from 'react'
import PropTypes from 'prop-types';


function YoutubeVideo({ videoSource, title, description }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleGetTestKit = () => {
    // Open the RequestModal which contains the HIV risk assessment
    document.getElementById('request_modal').showModal();
  };

  // Add rel=0 parameter to disable suggestions from other channels
  const getVideoSourceWithParams = (source) => {
    const separator = source.includes('?') ? '&' : '?';
    return `${source}${separator}rel=0`;
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        
        {/* Left Side - Info and Description */}
        <div className="space-y-8">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
              {title}
            </h2>
            {description && (
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                {description}
              </p>
            )}
          </div>

          {/* Features List */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-gray-700 text-lg">Free and confidential testing</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-gray-700 text-lg">Easy to use at home</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-gray-700 text-lg">Quick and accurate results</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-gray-700 text-lg">Professional support available</span>
            </div>
          </div>

          {/* Call to Action */}
          <div className="space-y-4">
            <button 
              onClick={handleGetTestKit}
              className="bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg text-lg cursor-pointer"
            >
              Get Your Test Kit Today
            </button>
            <p className="text-gray-500 text-sm">
              Want to learn more? Watch the video to see how it works.
            </p>
          </div>
        </div>

        {/* Right Side - Video */}
        <div className="relative">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Video Wrapper */}
            <div className="relative w-full pb-[56.25%] bg-gray-100">
              {/* Loading State */}
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
                    <p className="text-gray-600 font-medium">Loading video...</p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {hasError && (
                <div className="absolute inset-0 flex items-center justify-center bg-red-50">
                  <div className="text-center px-6 py-8">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-red-800 mb-2">Video Unavailable</h3>
                    <p className="text-red-600">Sorry, we couldn't load this video. Please try again later.</p>
                  </div>
                </div>
              )}

              {/* YouTube Iframe */}
              <iframe
                className="absolute inset-0 w-full h-full"
                src={getVideoSourceWithParams(videoSource)}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                style={{ display: hasError ? 'none' : 'block' }}
              />
            </div>

            {/* Video Info Bar */}
            <div className="p-4 bg-gray-50">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a2.997 2.997 0 0 0-2.114-2.114C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.384.527A2.997 2.997 0 0 0 .502 6.186C0 8.064 0 12 0 12s0 3.936.502 5.814a2.997 2.997 0 0 0 2.114 2.114c1.879.527 9.384.527 9.384.527s7.505 0 9.384-.527a2.997 2.997 0 0 0 2.114-2.114C24 15.936 24 12 24 12s0-3.936-.502-5.814zM9.636 15.568V8.432L15.432 12l-5.796 3.568z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-sm">Educational Video</p>
                  <p className="text-xs text-gray-500">Click to play</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


YoutubeVideo.propTypes = {
  videoSource: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
}

export default YoutubeVideo
